import type { Language } from '@/ui/i18n';
import { systemMassKg } from '@/physics/bike';

export type VsStrategy = 'A' | 'B';
export type UploadPolicy = 'auto' | 'ask' | 'off';
export type QualityPreset = 'low' | 'medium' | 'high';
export type RideMode = 'erg' | 'sim' | 'vs';

/** Actions that controller buttons / keys can be mapped to (SPEC §5.1). */
export type ControlAction =
  'shiftUp' | 'shiftDown' | 'ergUp' | 'ergDown' | 'skip' | 'pause' | 'lap' | 'camera' | 'none';

/**
 * Where the current FTP comes from. The rider enters it exactly once (onboarding); after that only a
 * completed FTP test changes it.
 */
export type FtpSource = 'onboarding' | 'test';

export interface Settings {
  displayName: string;
  ftp: number;
  /** When the FTP was last set (Unix ms); 0 = never → the app asks for it once. */
  ftpSetAt: number;
  ftpSource: FtpSource | null;
  riderWeightKg: number;
  /** 0–100 % */
  trainerDifficultyPct: number;
  vsStrategy: VsStrategy;
  defaultFreeRideMode: RideMode;
  autoPause: boolean;
  uploadPolicy: UploadPolicy;
  quality: QualityPreset;
  language: Language;
  /** Zwift Click module is experimental and behind this flag (SPEC §5.1). */
  clickEnabled: boolean;
  clickEncryptedHandshake: boolean;
  /** Button name (from the controller) → action. */
  buttonMapping: Record<string, ControlAction>;
  vsDebugOverlay: boolean;
  /** Rider color for avatar / multiplayer. */
  riderColor: string;
  updatedAt: number;
}

export const FTP_MIN = 50;
export const FTP_MAX = 600;

export const DEFAULT_SETTINGS: Settings = {
  displayName: '',
  ftp: 200,
  ftpSetAt: 0,
  ftpSource: null,
  riderWeightKg: 75,
  trainerDifficultyPct: 50,
  vsStrategy: 'A',
  defaultFreeRideMode: 'vs',
  autoPause: true,
  uploadPolicy: 'ask',
  quality: 'low',
  language: 'de',
  clickEnabled: false,
  clickEncryptedHandshake: false,
  buttonMapping: {
    'click.plus': 'shiftUp',
    'click.minus': 'shiftDown',
  },
  vsDebugOverlay: false,
  riderColor: '#e63946',
  updatedAt: 0,
};

/**
 * Former settings that are now fixed values (physics/bike.ts). They are dropped from stored and synced
 * settings so an old device can't bring them back.
 */
const RETIRED_KEYS = [
  'bikeWeightKg',
  'cda',
  'chainringTeeth',
  'cogTeeth',
  'wheelCircumferenceM',
  'gears',
  'draft',
] as const;

/** Merge stored (possibly older-schema) settings over defaults. */
export function normalizeSettings(stored: Partial<Settings> | undefined): Settings {
  const s = { ...DEFAULT_SETTINGS, ...(stored ?? {}) };
  for (const k of RETIRED_KEYS) delete (s as Record<string, unknown>)[k];
  s.buttonMapping = { ...DEFAULT_SETTINGS.buttonMapping, ...(stored?.buttonMapping ?? {}) };
  s.trainerDifficultyPct = Math.min(100, Math.max(0, s.trainerDifficultyPct));
  if (!Number.isFinite(s.ftpSetAt) || s.ftpSetAt < 0) s.ftpSetAt = 0;
  return s;
}

/** True until the rider has entered an FTP once (or a test has set one). */
export function needsFtp(s: Pick<Settings, 'ftpSetAt'>): boolean {
  return !(s.ftpSetAt > 0);
}

export function isValidFtp(w: number): boolean {
  return Number.isFinite(w) && w >= FTP_MIN && w <= FTP_MAX;
}

/** Name to show for the local rider: the one set in settings, else the Strava account name. */
export function resolveDisplayName(local: string, accountName?: string | null): string {
  return local.trim() || accountName?.trim() || '';
}

/** Rider + the fixed standard bike (kg). */
export function totalMassKg(s: Pick<Settings, 'riderWeightKg'>): number {
  return systemMassKg(s.riderWeightKg);
}
